import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { BreadcrumbView } from '../../../../context/breadcrumb.context'
import { IconList } from '../../../../shared/icon/icon'
import { useLoad } from '../../../../shared/hooks/load'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuDetails } from '../../../../shared/menu/menu'
import { REQUEST_LOG_MAX_MS } from '../../../../shared/audit/trace-links'
import { viaText } from '../../../../shared/audit/entry-format'
import { listRequests, slowRoutes } from './request-log-api'
import { SlowRoutes } from './slow-routes'
import { RequestDetailRoute } from './request-detail'
import { HealthBanner } from '../../../../shared/audit/health-banner'
import { formatMs, formatUTC, msClass, routeLabel, statusTone } from './format'
import { AppCell, ORIGIN_CHOICES } from './origin'
import { traceChip, traceLabel, traceShort } from './trace-kind'
import {
  aroundOf,
  aroundWindow,
  AROUND_CHOICES,
  defaultRange,
  filtersFromSearch,
  instantText,
  isExact,
  momentText,
  rangeOf,
  rangesFor,
  ROUTES_MAX_MS,
  searchWithFilters,
  spanOf,
  URL_KEYS,
  windowFor,
  windowText
} from './request-log-view'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import './request-log.scss'

// App versions in use (owner 2026-10-09): the platform's, under the log it
// counts. Loaded lazily, so ECharts stays in the Insights chunk.
const VersionsPanel = React.lazy(() => import('../../../../shared/audit/insights/versions-panel'))
const PLATFORM = { scope: 'all' }

export { msClass, filtersFromSearch, searchWithFilters }
export { RANGES } from './request-log-view'

// The grid's block size (TableAgInfinite cacheBlockSize); the server takes
// up to 500 a page.
export const PAGE_SIZE = 200

// The view's URL keys (request-log-view.js) plus the filters only the page
// holds: the minimum duration, the service, a slow route and the origin
// (content's ?unidentified=: requests whose app nothing names, origin.js).
const NO_FILTERS = { status: '', minMs: '', service: '', route: null, errors: false, unidentified: '', range: '', session: '', request: '', trace: '', ip: '', from: '', to: '' }
const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx']
// A change to any of these in the URL (a trace link) is a new view.
const SCOPE_KEYS = ['session', 'request', 'trace', 'ip', 'from', 'to']

const fromURL = search => ({ ...NO_FILTERS, ...filtersFromSearch(search) })
const servicesOf = rows => Array.from(new Set((rows || []).map(r => r.service).filter(Boolean)))
const shortID = v => `${String(v).slice(0, 8)}…`
const iso = ms => new Date(ms).toISOString()

// The filters as API parameters (empty ones are dropped by the query string).
const filterParams = filters => ({
  status: filters.status,
  errors: filters.errors ? '1' : '',
  unidentified: filters.unidentified,
  minMs: filters.minMs,
  service: filters.route ? filters.route.service : filters.service,
  route: filters.route ? filters.route.route : '',
  method: filters.route ? filters.route.method : '',
  sessionID: filters.session,
  requestID: filters.request,
  trace: filters.trace,
  ip: filters.ip
})

// How many more reads one block makes to fill itself from short pages.
export const MAX_FOLLOW = 10

// The grid's datasource for one set of filters. The API pages by keyset
// cursor, newest first, while the infinite row model asks for rows by
// offset: each block's cursor is the `next` of the block before it. The
// first block names the window (from/to); every later one passes only the
// cursor, which carries that window, so all pages read the same window. The
// row count stays open (-1) until a page comes back without a `next`.
//
// A page can be shorter than asked (even empty) and still carry `next`:
// with the Origin filter content scans in batches under a time budget. The
// block then keeps reading from that cursor until it is full or a page ends
// the list, at most MAX_FOLLOW more reads; a block still short after them
// leaves its cursor where the grid's next block starts, so scrolling on
// carries on reading.
export const makeRequestRows = ({ load, isCurrent, window }) => {
  const cursors = new Map()

  return async (start, end, search) => {
    const limit = Math.min(end - start, PAGE_SIZE)
    const q = search || undefined
    const params = { limit, q }
    if (start === 0) {
      Object.assign(params, window(), { before: undefined })
    } else {
      const before = cursors.get(start)
      if (!before) return { data: [], count: start }
      params.before = before
    }
    if (params.q === undefined) delete params.q

    let page = (await load(params)) || { items: [] }
    if (!isCurrent()) return undefined
    let items = page.items || []

    for (let follow = 0; page.next && items.length < limit && follow < MAX_FOLLOW; follow++) {
      const more = { limit: limit - items.length, q, before: page.next }
      if (more.q === undefined) delete more.q
      page = (await load(more)) || { items: [] }
      if (!isCurrent()) return undefined
      items = items.concat(page.items || [])
    }

    if (page.next) {
      cursors.set(start + items.length, page.next)
      if (items.length < limit) cursors.set(start + limit, page.next)
    }
    return { data: items, count: page.next ? -1 : start + items.length, page }
  }
}

// Whether a session has requests just outside the window the list reads
// (the log reads at most 31 days at once): one row each side, this session
// only. After the window only when it ends before now. windowKey names the
// window (a preset's instants move with now, so they can't).
const useOutsideWindow = (filters, windowKey) => {
  const [outside, setOutside] = useState(null)
  const session = filters.session
  const key = session ? `${session}|${windowKey}` : ''
  const latest = useRef(filters)
  latest.current = filters
  useEffect(() => {
    setOutside(null)
    if (!session) return undefined
    let live = true
    const { from, to } = windowFor(latest.current)
    const now = Date.now()
    const has = (a, b) => listRequests({ sessionID: session, from: a, to: b, limit: 1 }).then(p => Boolean(p && p.items && p.items.length), () => false)
    const before = { from: iso(Date.parse(from) - REQUEST_LOG_MAX_MS), to: from }
    const after = isExact(latest.current) && Date.parse(to) < now ? { from: to, to: iso(Math.min(Date.parse(to) + REQUEST_LOG_MAX_MS, now)) } : null
    Promise.all([has(before.from, before.to), after ? has(after.from, after.to) : false]).then(([b, a]) => {
      if (live) setOutside({ from, to, before: b ? before : null, after: a ? after : null })
    })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  return outside
}

// The Request log (audit-trail spec §5): every API request, with who made it,
// from which app and where, newest first. The list is the table-builder's
// server-paged grid; its search box searches paths, routes and people, and
// request ids: the full id or its first 8 or more characters (what an
// error's "Reference for support" and the Request ID column show).
// Opening a request shows it in the drawer at /admin/request-log/:requestID,
// which takes the start of an id too (request-detail.js).
//
// The URL holds the view (request-log-view.js), so the trace links (one
// session, one request, around a moment) and bookmarks open it as it was:
// a session, a request and an exact window each show as a chip, the summary
// above the grid says what the list shows, and the range preset never
// replaces an exact window unless the reader picks one.
export const RequestLogPage = () => {
  const history = useHistory()
  const location = useLocation()

  const [filters, setFilters] = useState(() => fromURL(location.search))
  const [error, failure, setError] = useErrorLine()
  const [geoUnavailable, setGeoUnavailable] = useState(false)
  const [routes, setRoutes] = useState(undefined)
  const [routesError, routesFailure, setRoutesError] = useErrorLine()

  const tableRef = useRef()

  const exact = isExact(filters)
  const scoped = Boolean(filters.session || filters.request || filters.trace)
  const preset = rangeOf(filters)
  const windowKey = exact ? `${filters.from}_${filters.to}` : `range:${preset.value}`
  // The slowest routes can't narrow to one session or request, and cover at
  // most 7 days (content maxRoutes).
  const routesReadable = !scoped && (!exact || spanOf(filters.from, filters.to) <= ROUTES_MAX_MS)

  // Every service the window saw. The slowest routes narrow to the service
  // picked, so the list is read with no service filter (the server takes one
  // service at a time); otherwise picking one would leave only that one.
  // Without the slowest routes (one session or request), the services the
  // list itself has shown, until the session, request or window changes.
  const [known, setKnown] = useState({ key: null, list: [] })
  const [seen, setSeen] = useState([])
  const knownKey = useRef(null)
  knownKey.current = known.key
  const windowNow = useRef(windowKey)
  windowNow.current = windowKey
  const scopeKey = `${windowKey}|${filters.session}|${filters.request}|${filters.trace}`
  useEffect(() => setSeen([]), [scopeKey])

  // The slowest routes for the window (and service): undefined while loading.
  const routesKey = `${windowKey}:${filters.service}:${routesReadable}`
  useLoad(async () => {
    setRoutes(undefined)
    setRoutesError(null)
    if (!routesReadable) return
    try {
      const rows = (await slowRoutes({ ...windowFor(filters), service: filters.service })) || []
      setRoutes(rows)
      if (!filters.service && windowNow.current === windowKey) setKnown({ key: windowKey, list: servicesOf(rows) })
    } catch (err) {
      setRoutes(null)
      setRoutesError(serverMessage(err, "The slowest routes couldn't be loaded."), err)
    }
  }, [routesKey])

  // A new window with a service already picked: read every service for it.
  useLoad(async () => {
    if (!routesReadable || !filters.service || knownKey.current === windowKey) return
    try {
      const rows = (await slowRoutes(windowFor(filters))) || []
      if (windowNow.current === windowKey) setKnown({ key: windowKey, list: servicesOf(rows) })
    } catch (err) {
      // The list keeps the services it had; the routes read reports errors.
    }
  }, [windowKey, Boolean(filters.service), routesReadable])

  const services = useMemo(() => {
    const set = new Set(routesReadable ? known.list : seen)
    if (filters.service) set.add(filters.service)
    return Array.from(set).sort()
  }, [known, seen, routesReadable, filters.service])

  const gridKey = JSON.stringify(filters)

  // A response for filters the page has moved on from never lands.
  const currentKey = useRef(gridKey)
  currentKey.current = gridKey

  const getRows = useMemo(
    () => {
      const mine = gridKey
      const fetchRows = makeRequestRows({
        isCurrent: () => currentKey.current === mine,
        window: () => windowFor(filters),
        load: params => listRequests({ ...filterParams(filters), ...params })
      })

      return async (start, end, search) => {
        try {
          setError(null)
          const result = await fetchRows(start, end, search)
          if (result && result.page) setGeoUnavailable(Boolean(result.page.geoUnavailable))
          if (result && !routesReadable) {
            const found = servicesOf(result.data)
            setSeen(s => (found.every(x => s.includes(x)) ? s : Array.from(new Set([...s, ...found]))))
          }
          return result && { data: result.data, count: result.count }
        } catch (err) {
          if (currentKey.current !== mine) return undefined
          setError(serverMessage(err, start === 0 ? "The request log couldn't be loaded." : "Older requests couldn't be loaded."), err)
          return { data: [], count: start }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridKey]
  )

  const set = (key, value) => setFilters(f => ({ ...f, [key]: value }))

  // The URL is the view: the menu's REQUEST LOG / ERROR LOG, Back/Forward and
  // the trace links change it without remounting the page. A new session,
  // request or window is a new view (the filters the URL doesn't hold go
  // too, so the link shows what it says); a status change keeps them. A URL
  // that already matches (the write-back below) changes nothing, so the two
  // never loop.
  useEffect(() => {
    const f = filtersFromSearch(location.search)
    setFilters(cur => {
      if (URL_KEYS.every(k => cur[k] === f[k])) return cur
      return SCOPE_KEYS.some(k => cur[k] !== f[k]) ? { ...NO_FILTERS, ...f } : { ...cur, ...f }
    })
  }, [location.search])

  // The filters the URL keeps go back to it (replace: a filter change is not
  // a page the Back button should step through).
  useEffect(() => {
    const search = searchWithFilters(location.search, filters)
    if (search !== location.search) history.replace({ pathname: location.pathname, search, state: location.state })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, URL_KEYS.map(k => filters[k]))

  const open = row => row && history.push({ pathname: `/admin/request-log/${row.requestID}`, search: location.search })

  // A preset is the reader's choice: it replaces an exact window (a session
  // or request stays).
  const pickRange = value => {
    if (value === 'exact') return
    setFilters(f => ({ ...f, from: '', to: '', range: value === defaultRange(f) ? '' : value }))
  }
  // Removing a session, a request or the window goes back to the default range.
  const unscope = patch => setFilters(f => ({ ...f, ...patch, from: '', to: '', range: '' }))
  const showWindow = w => setFilters(f => ({ ...f, from: w.from, to: w.to, range: '' }))

  const around = aroundOf(filters)
  const outside = useOutsideWindow(filters, windowKey)

  // the address is a narrowing too, but the summary names it on its own
  const narrowedBesidesAddress = Boolean(filters.status || filters.errors || filters.unidentified || filters.minMs || filters.service || filters.route)
  const narrowed = narrowedBesidesAddress || Boolean(filters.ip)
  const filtered = narrowed || scoped || exact

  const clear = () => setFilters(NO_FILTERS)

  // A failed read is not an empty log: the grid says so too (the server's
  // own sentence sits above it).
  const emptyState = error ? { Icon: IconList, title: "Couldn't load the requests", message: 'See the message above the list.' } : {
    Icon: IconList,
    title: 'No requests',
    message: filters.session && !narrowed
      ? 'This session has no requests in this time window.'
      : filters.trace && !narrowed
        ? 'This sign-in attempt has no requests in this time window.'
        : filtered ? 'Nothing matches these filters.' : 'No requests were recorded in this time range.',
    action: { label: 'Clear filters', onClick: clear, show: filtered }
  }

  return (
    <BreadcrumbView Icon={IconList} text="REQUEST LOG">
      <FleetPage
        title="Request log"
        purpose="Every API request, with who made it, from which app and where. Headers and bodies are shown with secrets removed.">
        {/* super admins only (it reads nothing for anyone else); the location
            problem is already the notice below */}
        <HealthBanner omit={['geo_unavailable']} />
        {geoUnavailable &&
          <Notice tone="warn" role="status">Locations aren't available: this server has no IP location database installed.</Notice>}

        <div className="sw-toolbar rl-toolbar">
          <select className="form-select form-select-sm" aria-label="Time range" value={exact ? 'exact' : preset.value} onChange={e => pickRange(e.target.value)}>
            {exact && <option value="exact">Exact window</option>}
            {rangesFor(filters).map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="Status" value={filters.status} onChange={e => set('status', e.target.value)}>
            <option value="">Any status</option>
            {STATUS_CLASSES.map(s => <option key={s} value={s}>{s}</option>)}
            {/* an exact status from a link (?status=503) */}
            {filters.status && !STATUS_CLASSES.includes(filters.status) && <option value={filters.status}>{filters.status}</option>}
          </select>
          <label className="form-check rl-errors">
            <input type="checkbox" className="form-check-input" checked={filters.errors} onChange={e => set('errors', e.target.checked)} />
            <span className="form-check-label">Only requests with an error</span>
          </label>
          <select className="form-select form-select-sm" aria-label="Origin" value={filters.unidentified} onChange={e => set('unidentified', e.target.value)}>
            {ORIGIN_CHOICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="form-select form-select-sm" aria-label="Service" value={filters.route ? filters.route.service : filters.service} disabled={Boolean(filters.route)} onChange={e => set('service', e.target.value)}>
            <option value="">Every service</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <IPFilter value={filters.ip} onChange={ip => set('ip', ip)} />
          <span className="rl-min-ms">
            <label htmlFor="rl-min-ms">Slower than (ms)</label>
            <input id="rl-min-ms" type="number" min="0" step="50" inputMode="numeric" className="form-control form-control-sm" value={filters.minMs} onChange={e => set('minMs', e.target.value)} />
          </span>
          {filters.route &&
            <button type="button" className="sw-chip" aria-label={`Remove the route filter ${filters.route.method} ${filters.route.route}`} onClick={() => set('route', null)}>
              {filters.route.method} {filters.route.route} <span aria-hidden="true">✕</span>
            </button>}
          {filters.session &&
            <button type="button" className="sw-chip" aria-label={`Remove the session filter ${filters.session}`} onClick={() => unscope({ session: '' })}>
              Session {shortID(filters.session)} <span aria-hidden="true">✕</span>
            </button>}
          {filters.request &&
            <button type="button" className="sw-chip" aria-label={`Remove the request filter ${filters.request}`} onClick={() => unscope({ request: '' })}>
              Request {shortID(filters.request)} <span aria-hidden="true">✕</span>
            </button>}
          {filters.trace &&
            <button type="button" className="sw-chip" aria-label={`Remove the ${traceLabel(filters.trace)} filter`} onClick={() => unscope({ trace: '' })}>
              {traceChip(filters.trace)} <span aria-hidden="true">✕</span>
            </button>}
          {exact &&
            <button type="button" className="sw-chip" aria-label={`Remove the time window ${windowText(filters.from, filters.to)}`} onClick={() => unscope({})}>
              {windowText(filters.from, filters.to)} <span aria-hidden="true">✕</span>
            </button>}
        </div>

        {(scoped || exact) &&
          <p className="rl-summary" data-testid="request-log-summary">
            Showing {filters.session
              ? <>{narrowed ? 'the requests' : 'every request'} in session <code title={filters.session}>{shortID(filters.session)}</code>{narrowed ? ' that match these filters' : ''}</>
              : filters.request
                ? <>request <code title={filters.request}>{shortID(filters.request)}</code></>
                : filters.trace
                  ? <>{narrowed ? 'the requests' : 'every request'} in {traceLabel(filters.trace)} <code title={filters.trace}>{traceShort(filters.trace)}</code>{narrowed ? ' that match these filters' : ''}</>
                  : filters.ip
                    ? <>{narrowedBesidesAddress ? 'the requests' : 'every request'} from <code>{filters.ip}</code>{narrowedBesidesAddress ? ' that match these filters' : ''}</>
                    : narrowed ? 'the requests that match these filters' : 'every request from every service and person'}
            {exact
              ? `, ${windowText(filters.from, filters.to)}`
              : ` in the ${preset.label.toLowerCase()}${preset.ms === REQUEST_LOG_MAX_MS ? ', the most the log reads at once' : ''}`}.
          </p>}

        {around &&
          <div className="rl-around" role="group" aria-label={`Around ${momentText(around.at)}`}>
            <span className="rl-around-label" aria-hidden="true">{`Around ${momentText(around.at)}:`}</span>
            {AROUND_CHOICES.map(c =>
              <button key={c.ms} type="button" className="btn sw-btn btn-sm" aria-pressed={Math.abs(around.ms - c.ms) < 1} onClick={() => showWindow(aroundWindow(around.at, c.ms))}>
                {`${c.label} either side`}
              </button>)}
          </div>}

        {outside && (outside.before || outside.after) &&
          <div className="rl-outside">
            {outside.before &&
              <Notice tone="warn" role="status">
                <p>
                  This session also has requests before {instantText(outside.from)}. The log reads at most 31 days at once.{' '}
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => showWindow(outside.before)}>Show the 31 days before</button>
                </p>
              </Notice>}
            {outside.after &&
              <Notice tone="warn" role="status">
                <p>
                  This session also has requests after {instantText(outside.to)}. The log reads at most 31 days at once.{' '}
                  <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => showWindow(outside.after)}>
                    {spanOf(outside.after.from, outside.after.to) >= REQUEST_LOG_MAX_MS ? 'Show the 31 days after' : 'Show from then until now'}
                  </button>
                </p>
              </Notice>}
          </div>}

        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mb-2" />}

        <FleetGrid title="requests" subtitle="Newest first" tableRef={tableRef} exportName="request-log">
          <TableAgInfinite
            pinFirstColumn
            key={gridKey}
            ref={tableRef}
            queryKey=""
            onGetRows={getRows}
            onRowClicked={open}
            emptyState={emptyState}
            noMatchState={{ Icon: IconList, title: 'No matches', message: 'Nothing matches this search.' }}>
            <ColumnFormat field="occurredAt" header="When" render={whenCell} sortable={false} minWidth={130} />
            <ColumnText field="method" header="Method" sortable={false} minWidth={90} flex={0.6} />
            <ColumnFormat field="route" header="Route" render={routeCell} sortable={false} minWidth={240} flex={3} />
            <ColumnFormat field="status" header="Status" render={statusCell} sortable={false} minWidth={150} flex={0.8} />
            <ColumnFormat field="durationMs" header="Duration" render={durationCell} sortable={false} minWidth={100} flex={0.7} />
            <ColumnFormat field="actorEmail" header="Who" render={whoCell} sortable={false} minWidth={200} flex={2} />
            <ColumnFormat field="clientApp" header="App" render={appCell} sortable={false} minWidth={150} flex={1.4} />
            <ColumnFormat field="ip" header="IP" render={ipCell} sortable={false} minWidth={150} flex={1.2} />
            <ColumnText field="service" header="Service" sortable={false} minWidth={100} flex={0.8} />
            <ColumnFormat field="requestID" header="Request ID" render={requestIDCell} sortable={false} minWidth={110} flex={0.8} />
            <ColumnMenu
              menu={r =>
                <Menu small>
                  <MenuDetails text="details" pin show onClick={() => open(r)} />
                </Menu>}
            />
          </TableAgInfinite>
        </FleetGrid>

        {routesReadable
          ? <>
              {routesError && <p className="text-danger small" role="alert">{routesError}</p>}
              {routesError && <ErrorReference error={routesFailure} className="mb-2" />}
              <SlowRoutes rows={routes} onPick={r => setFilters(f => ({ ...f, route: { service: r.service, method: r.method, route: r.route } }))} />
            </>
          : <p className="small sw-hint rl-routes-hidden">
              {scoped
                ? `The slowest routes cover every request, so they're hidden while the list shows one ${filters.session ? 'session' : filters.trace ? traceLabel(filters.trace) : 'request'}.`
                : 'The slowest routes cover at most 7 days; choose a shorter window to see them.'}
            </p>}

        <section className="au-ins rl-versions" aria-label="App versions in use">
          <Suspense fallback={<p className="sw-hint">Loading…</p>}>
            <VersionsPanel params={PLATFORM} question="Which versions of each app are people using, how fast is a new one adopted, and who is still on an old one? Every organization." />
          </Suspense>
        </section>

        <p className="small sw-hint rl-credit">IP location by <a href="https://db-ip.com" target="_blank" rel="noreferrer">DB-IP</a></p>
      </FleetPage>

      <RequestDetailRoute />
    </BreadcrumbView>
  )
}

// The address filter: an IP address or a CIDR range (content checks it and
// says so when it isn't one). Applied on Enter or when the box is left, so
// a half-typed address is never sent; an empty box clears it.
const IP_SHAPE = /^[0-9A-Fa-f:.]{2,45}(\/\d{1,3})?$/
const IPFilter = ({ value, onChange }) => {
  const [draft, setDraft] = useState(value || '')
  useEffect(() => setDraft(value || ''), [value])
  const apply = () => {
    const v = draft.trim()
    if (v === (value || '')) return
    if (v === '' || IP_SHAPE.test(v)) onChange(v)
  }
  const invalid = draft.trim() !== '' && !IP_SHAPE.test(draft.trim())
  return (
    <span className="rl-ip">
      <label htmlFor="rl-ip">IP address</label>
      <input
        id="rl-ip"
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        placeholder="e.g. 203.0.113.9"
        className={`form-control form-control-sm sw-mono${invalid ? ' is-invalid' : ''}`}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? 'rl-ip-hint' : undefined}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={apply}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply() } }} />
      {invalid && <span id="rl-ip-hint" className="sw-hint small">An IP address or a range like 203.0.113.0/24</span>}
    </span>
  )
}

const whenCell = row => (row.data ? <span title={formatUTC(row.value)}>{formatAgo(row.value)}</span> : '')

const routeCell = row => (row.data ? <span className="sw-mono" title={row.data.path}>{routeLabel(row.data)}</span> : '')

// A request that recorded an error carries a "bad" (5xx) pill beside its status.
const statusCell = row => {
  if (!row.data) return ''
  return (
    <span className="rl-status">
      <Pill tone={statusTone(row.value)}>{String(row.value)}</Pill>
      {row.data.hasError && <Pill tone="bad" title="This request recorded an error">error</Pill>}
    </span>
  )
}

const durationCell = row => (row.data ? <span className={msClass(row.value)}>{formatMs(row.value)}</span> : '')

const whoCell = row => {
  if (!row.data) return ''
  const who = row.data.actorEmail || row.data.actorName
  // a learner's bounded LMS session reads "via LMS connection <name>" (F-11, final review M7)
  const via = viaText(row.data)
  return (
    <span>
      {who || <span className="sw-hint">Anonymous</span>}
      {row.data.impersonatorLoginID ? <span className="sw-hint"> (impersonated)</span> : null}
      {via ? <span className="sw-hint"> {via}</span> : null}
    </span>
  )
}

// The stored app; else the app content inferred when the log was read
// (muted, marked), or the flag for one nothing names (origin.js).
const appCell = row => (row.data ? <AppCell r={row.data} /> : '')

const ipCell = row => (row.data && row.value ? <span className="sw-mono">{row.value}{row.data.country ? <span className="sw-hint"> {row.data.country}</span> : null}</span> : '')

const requestIDCell = row => (row.data ? <span className="sw-mono" title={row.value}>{String(row.value || '').slice(0, 8)}</span> : '')
